/*
 * A small toast system. Toasts report things that really happened
 * (a request was accepted, an operation finished), never optimistic success.
 * They look like the other notices (ui/Notice), in the same four status tones
 * whatever the brand: green for done (good), amber for something that needs
 * attention (warning), red for a problem (critical), blue for information.
 * The tone's icon, a short title, a line, one action.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { cx } from "../lib/cx";
import { noteProblemToast } from "../lib/errorShown";
import { IconAlert, IconAlertCircle, IconCheckCircle, IconInfo, IconX } from "./icons";

/** good (or "success"), warning, critical (or "error"), info. */
export type ToastTone = "good" | "warning" | "critical" | "info" | "success" | "error";

const TOAST_TONE = {
  good: { icon: IconCheckCircle, cls: "text-good" },
  warning: { icon: IconAlert, cls: "text-warn" },
  critical: { icon: IconAlertCircle, cls: "text-bad" },
  info: { icon: IconInfo, cls: "text-info" },
} as const;
const toneOf = (t: ToastTone | undefined): keyof typeof TOAST_TONE => (t === "success" ? "good" : t === "error" ? "critical" : (t ?? "info"));
export type ToastInput = {
  title: string;
  description?: ReactNode;
  tone?: ToastTone;
  action?: { label: string; onClick: () => void };
  /** Milliseconds; errors stay longer by default. */
  duration?: number;
};
type ToastItem = ToastInput & { id: number };

const ToastContext = createContext<((t: ToastInput) => void) | null>(null);

export function useToast() {
  const fn = useContext(ToastContext);
  if (!fn) throw new Error("useToast must be used inside <ToastProvider>");
  return fn;
}

let nextId = 1;

/*
 * Toasts never cover a bar's buttons. A bar at the bottom of the screen (a save bar, the Sites page's selection, a
 * dialog's or sheet's footer) carries `data-toast-lift`; while toasts are up, they sit just above the highest such bar
 * that's under them (on screen, across the same width, and where the toasts would be). Nothing else has to know.
 */
function useLift(active: boolean, region: RefObject<HTMLDivElement | null>) {
  const [lift, setLift] = useState(0);
  useEffect(() => {
    if (!active) {
      setLift(0);
      return;
    }
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const box = region.current?.getBoundingClientRect();
        if (!box) return;
        const h = window.innerHeight;
        let next = 0;
        for (const el of document.querySelectorAll<HTMLElement>("[data-toast-lift]")) {
          const r = el.getBoundingClientRect();
          if (!r.width || !r.height || r.top >= h || r.bottom <= 0) continue; // hidden, or off screen
          if (r.right <= box.left || r.left >= box.right) continue; // beside the toasts, not under them
          if (r.bottom <= h - box.height) continue; // above where the toasts sit
          next = Math.max(next, Math.round(h - r.top - 8));
        }
        setLift(next);
      });
    };
    measure();
    // Bars come and go (a save bar appears with a change), and sticky ones move as the page scrolls.
    const mo = new MutationObserver(measure);
    mo.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ["data-toast-lift", "class"] });
    window.addEventListener("scroll", measure, { capture: true, passive: true });
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(frame);
      mo.disconnect();
      window.removeEventListener("scroll", measure, { capture: true });
      window.removeEventListener("resize", measure);
    };
  }, [active, region]);
  return lift;
}

/** Marks a bar toasts should stay above, while `on` (for bars that are always in the page but not always shown). */
export function useToastLift(ref: RefObject<HTMLElement | null>, on: boolean) {
  useEffect(() => {
    const el = ref.current;
    if (!on || !el) return;
    el.setAttribute("data-toast-lift", "");
    return () => el.removeAttribute("data-toast-lift");
  }, [ref, on]);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const region = useRef<HTMLDivElement>(null);
  const lift = useLift(items.length > 0, region);

  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const push = useCallback((t: ToastInput) => {
    const id = nextId++;
    if (toneOf(t.tone) === "critical") noteProblemToast(); // a failure said here needs no fallback toast (api/queryClient.ts)
    setItems((xs) => [...xs.slice(-3), { ...t, id }]);
  }, []);

  const value = useMemo(() => push, [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Two live regions: polite for news, assertive for errors. */}
      <div
        ref={region}
        aria-live="polite"
        aria-relevant="additions"
        style={{ bottom: lift }}
        className="pointer-events-none fixed right-0 bottom-0 z-[70] flex w-full flex-col items-end gap-2.5 p-4 transition-[bottom] duration-150 sm:max-w-[420px] sm:p-5"
      >
        {items.map((t) => (
          <ToastView key={t.id} item={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastView({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const tone = toneOf(item.tone);
  const duration = item.duration ?? (tone === "critical" ? 9000 : 5500);
  const [paused, setPaused] = useState(false);
  const remaining = useRef(duration);
  const started = useRef(Date.now());

  useEffect(() => {
    if (paused) return;
    started.current = Date.now();
    const t = setTimeout(onDismiss, remaining.current);
    return () => {
      clearTimeout(t);
      remaining.current -= Date.now() - started.current;
    };
  }, [paused, onDismiss]);

  const Icon = TOAST_TONE[tone].icon;

  return (
    <div
      role={tone === "critical" ? "alert" : undefined}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className="hx-toast pop pointer-events-auto flex w-full items-start gap-3 py-3.5 pr-2.5 pl-4"
    >
      <Icon
        size={18}
        className={cx("mt-px shrink-0", TOAST_TONE[tone].cls)}
      />
      <div className="min-w-0 flex-1">
        <p className="text-[14px] leading-5 font-semibold text-fg">{item.title}</p>
        {item.description ? <div className="mt-0.5 text-[13px] leading-relaxed text-body">{item.description}</div> : null}
        {item.action ? (
          <button
            type="button"
            onClick={() => {
              item.action?.onClick();
              onDismiss();
            }}
            className="link mt-1.5 text-[13px] font-semibold"
          >
            {item.action.label}
          </button>
        ) : null}
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onDismiss}
        // -my-1: the button sits in the title's 20px line, so it doesn't stretch the toast and leave the title off center.
        className="-my-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-dim hover:bg-hover hover:text-fg focus-visible:outline-2 focus-visible:outline-brand-500"
      >
        <IconX size={15} />
      </button>
    </div>
  );
}
